import React, { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import FormWithValidation from "./components/common/FormWithValidation";
import { createIncident, getIncidentAdminTaxonomy, updateIncident } from "./api/incidents.api";
import { listAdminUsers } from "./api/admin.api";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Textarea from "./ui/Textarea";
import { colors, spacing, typography } from "./ui/tokens";
import { surfaceCardBodyStyle, surfaceCardStyle } from "./ui/pagePatterns";

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional().nullable(),
  incident_type: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  severity: z.string().min(1, "Severity is required"),
  priority: z.string().optional().nullable(),
  occurred_at: z.string().optional().nullable(),
  owner_id: z.string().optional().nullable(),
});

const defaultTaxonomyOptions = {
  incident_type: [
    { code: "Operational", label: "Operational" },
    { code: "Security", label: "Security" },
    { code: "Privacy", label: "Privacy" },
    { code: "Compliance", label: "Compliance" },
  ],
  category: [
    { code: "IT", label: "IT" },
    { code: "Security", label: "Security" },
    { code: "ThirdParty", label: "Third Party" },
    { code: "Operations", label: "Operations" },
  ],
  severity: [
    { code: "Low", label: "Low" },
    { code: "Medium", label: "Medium" },
    { code: "High", label: "High" },
    { code: "Critical", label: "Critical" },
  ],
  priority: [
    { code: "Low", label: "Low" },
    { code: "Medium", label: "Medium" },
    { code: "High", label: "High" },
    { code: "Critical", label: "Critical" },
  ],
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 16);
};

export default function IncidentForm({ onSaved, onCancel, incident = null, defaultValues = {} }) {
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState(null);
  const [taxonomyOptions, setTaxonomyOptions] = useState(defaultTaxonomyOptions);
  const incidentId = incident?.id ?? defaultValues?.id ?? null;
  const isEdit = Boolean(incidentId);

  const defaults = useMemo(
    () => ({
      title: "",
      description: "",
      incident_type: "",
      category: "",
      severity: "Low",
      priority: "",
      occurred_at: "",
      owner_id: "",
      ...defaultValues,
      ...(incident
        ? {
            title: incident.title ?? "",
            description: incident.description ?? "",
            incident_type: incident.incident_type ?? "",
            category: incident.category ?? "",
            severity: incident.severity ?? "Low",
            priority: incident.priority ?? "",
            occurred_at: toInputDateTime(incident.occurred_at || incident.reported_at),
            owner_id: incident.owner_id ?? "",
          }
        : null),
    }),
    [defaultValues, incident]
  );

  const handleSubmit = async (values) => {
    setSaving(true);
    setServerFieldErrors({});
    setFormError(null);
    try {
      const payload = {
        title: values.title,
        description: values.description || null,
        incident_type: values.incident_type || null,
        category: values.category || null,
        severity: values.severity,
        priority: values.priority || null,
        owner_id: values.owner_id || null,
        occurred_at: values.occurred_at || null,
      };
      const res = isEdit
        ? await updateIncident(incidentId, payload)
        : await createIncident({ ...payload, status: "New" });
      onSaved?.(res.data || res);
    } catch (err) {
      setServerFieldErrors(err?.fieldErrors || {});
      setFormError(err?.detail || err?.message || "Failed to save incident");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadTaxonomy = async () => {
      try {
        const [typeRes, categoryRes, severityRes, priorityRes] = await Promise.all([
          getIncidentAdminTaxonomy("incident_type"),
          getIncidentAdminTaxonomy("category"),
          getIncidentAdminTaxonomy("severity"),
          getIncidentAdminTaxonomy("priority"),
        ]);
        if (!active) return;
        setTaxonomyOptions({
          incident_type: (typeRes.data || typeRes).filter((item) => item.active),
          category: (categoryRes.data || categoryRes).filter((item) => item.active),
          severity: (severityRes.data || severityRes).filter((item) => item.active),
          priority: (priorityRes.data || priorityRes).filter((item) => item.active),
        });
      } catch (_err) {
        if (active) {
          setTaxonomyOptions(defaultTaxonomyOptions);
        }
      }
    };
    const loadOwners = async () => {
      setOwnersLoading(true);
      setOwnersError(null);
      try {
        const res = await listAdminUsers();
        const users = res?.data || [];
        const options = users
          .filter((u) => u?.username)
          .map((u) => ({
            value: u.username,
            label: u.email ? `${u.username} (${u.email})` : u.username,
          }));
        if (active) {
          setOwnerOptions(options);
        }
      } catch (err) {
        if (active) {
          setOwnerOptions([]);
          setOwnersError(err?.response?.data?.detail || err?.message || "Unable to load owners.");
        }
      } finally {
        if (active) {
          setOwnersLoading(false);
        }
      }
    };
    loadTaxonomy();
    loadOwners();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div
      style={{
        ...surfaceCardStyle,
        ...surfaceCardBodyStyle,
      }}
    >
      <h3
        style={{
          marginTop: 0,
          marginBottom: spacing.md,
          color: colors.text,
          fontSize: typography.size.lg,
          fontWeight: typography.weight.bold,
        }}
      >
        {isEdit ? "Edit Incident" : "Report New Incident"}
      </h3>
      <FormWithValidation
        schema={schema}
        defaultValues={defaults}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitLabel={saving ? "Saving..." : isEdit ? "Save" : "Create"}
        serverFieldErrors={serverFieldErrors}
        formError={formError}
      >
        {({ register, formState }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
            <label>
              Title
              <Input error={formState.errors.title?.message} {...register("title")} />
            </label>
            <label>
              Description
              <Textarea rows={3} error={formState.errors.description?.message} {...register("description")} />
            </label>
            <label>
              Type
              <Select error={formState.errors.incident_type?.message} {...register("incident_type")}>
                <option value="">Select type</option>
                {taxonomyOptions.incident_type.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Category
              <Select error={formState.errors.category?.message} {...register("category")}>
                <option value="">Select category</option>
                {taxonomyOptions.category.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Severity
              <Select error={formState.errors.severity?.message} {...register("severity")}>
                {taxonomyOptions.severity.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Priority
              <Select error={formState.errors.priority?.message} {...register("priority")}>
                <option value="">No priority</option>
                {taxonomyOptions.priority.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label || option.code}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              Occurred At
              <Input type="datetime-local" error={formState.errors.occurred_at?.message} {...register("occurred_at")} />
            </label>
            <label>
              Owner
              <Select
                error={formState.errors.owner_id?.message}
                {...register("owner_id")}
                disabled={ownersLoading || ownerOptions.length === 0}
              >
                <option value="">Select owner</option>
                {ownerOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              {ownersError && ownerOptions.length === 0 && (
                <div style={{ color: colors.danger, fontSize: typography.size.xs }}>{ownersError}</div>
              )}
            </label>
          </div>
        )}
      </FormWithValidation>
    </div>
  );
}
